import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink, FileText, Play } from "lucide-react";
import { resolveMedia } from "@/lib/api";
import { detectExternalVideo, isExternalVideo } from "@/lib/videoUrl";

function fileKind(item) {
  if (!item) return "image";
  if (isExternalVideo(item)) return "video-url";
  if (item.type) return item.type;
  const u = (item.url || "").toLowerCase();
  if (u.match(/\.(mp4|webm|mov)$/)) return "video";
  if (u.endsWith(".pdf")) return "pdf";
  return "image";
}

function MediaThumb({ item, kind, onOpen, size = "small", index, total }) {
  const url = kind === "video-url" ? null : resolveMedia(item.url);
  const ext = isExternalVideo(item) ? detectExternalVideo(item.url) : null;
  const sizeClass = size === "large" ? "aspect-[16/9]" : "aspect-[4/3]";

  if (kind === "pdf") {
    return (
      <a href={resolveMedia(item.url)} target="_blank" rel="noreferrer" data-testid={`gallery-pdf-${index}`} className={`group block ${sizeClass} bg-neutral-100 border border-brand-line flex flex-col items-center justify-center text-center p-4 hover:bg-[hsl(var(--brand-soft))] hover:border-[hsl(var(--brand-green))] transition-colors`}>
        <FileText className={size === "large" ? "h-14 w-14 mb-3" : "h-8 w-8 mb-2"} strokeWidth={1.5} />
        <span className={`text-neutral-700 line-clamp-2 ${size === "large" ? "text-sm" : "text-xs"}`}>{item.caption || "PDF öffnen"}</span>
        <span className="mt-2 inline-flex items-center gap-1 text-[10px] text-neutral-500">PDF <ExternalLink className="h-3 w-3" /></span>
      </a>
    );
  }

  if (kind === "video-url") {
    return (
      <button onClick={onOpen} data-testid={`gallery-video-url-${index}`} className={`group relative ${sizeClass} bg-black overflow-hidden border border-brand-line w-full`}>
        {ext?.thumb ? (
          <img src={ext.thumb} alt={item.caption || ""} className="w-full h-full object-cover opacity-90" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-900 flex items-center justify-center text-white text-xs uppercase tracking-wider">{ext?.provider || "Video"}</div>
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
          <span className={`rounded-full bg-white/90 flex items-center justify-center ${size === "large" ? "h-16 w-16" : "h-12 w-12"}`}><Play className={`text-neutral-900 ml-0.5 ${size === "large" ? "h-7 w-7" : "h-5 w-5"}`} /></span>
        </span>
        {item.caption && <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[11px] px-3 py-1.5 truncate">{item.caption}</span>}
      </button>
    );
  }

  if (kind === "video") {
    return (
      <button onClick={onOpen} data-testid={`gallery-video-${index}`} className={`group relative ${sizeClass} bg-black overflow-hidden border border-brand-line w-full`}>
        <video src={url} className="w-full h-full object-cover opacity-90" muted playsInline preload="metadata" />
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
          <span className={`rounded-full bg-white/90 flex items-center justify-center ${size === "large" ? "h-16 w-16" : "h-12 w-12"}`}><Play className={`text-neutral-900 ml-0.5 ${size === "large" ? "h-7 w-7" : "h-5 w-5"}`} /></span>
        </span>
        {item.caption && <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[11px] px-3 py-1.5 truncate">{item.caption}</span>}
      </button>
    );
  }

  // image
  return (
    <button onClick={onOpen} data-testid={`gallery-image-${index}`} className={`group block ${sizeClass} overflow-hidden bg-neutral-100 border border-brand-line w-full`}>
      <img src={url} alt={item.caption || ""} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
      {size === "large" && item.caption && (
        <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-sm px-4 py-3">{item.caption}</span>
      )}
    </button>
  );
}

function PdfCard({ item, index }) {
  return (
    <a
      href={resolveMedia(item.url)}
      target="_blank"
      rel="noreferrer"
      data-testid={`gallery-pdf-${index}`}
      className="group flex items-stretch gap-4 border border-brand-line hover:border-[hsl(var(--brand-green))] hover:shadow-sm transition-all bg-white overflow-hidden"
    >
      <div className="w-20 sm:w-24 aspect-[3/4] flex-shrink-0 bg-neutral-100 overflow-hidden relative">
        {item.preview_url ? (
          <img
            src={resolveMedia(item.preview_url)}
            alt={item.caption || ""}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200">
            <FileText className="h-7 w-7 text-[hsl(var(--brand-green-strong))]" strokeWidth={1.5} />
          </div>
        )}
        <span className="absolute bottom-1.5 left-1.5 bg-white/95 backdrop-blur-sm text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--brand-green-strong))] px-1.5 py-0.5 font-medium">PDF</span>
      </div>
      <div className="flex-1 min-w-0 py-3.5 pr-4 flex flex-col justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-400 mb-1">Dokument</p>
          <p className="text-sm text-neutral-900 font-medium leading-snug line-clamp-2">{item.caption || "PDF zum Download"}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--brand-green-strong))] font-medium mt-2 group-hover:gap-2 transition-all">
          Öffnen <ExternalLink className="h-3 w-3" />
        </span>
      </div>
    </a>
  );
}

export function MediaGallery({ items, testid = "media-gallery" }) {
  const [openIdx, setOpenIdx] = useState(null);
  if (!items || items.length === 0) return null;

  // Separate visual items (images + videos) from PDFs so PDFs never end up
  // as a huge 16:9 header. PDFs render as compact document cards below.
  const visual = items.filter((it) => fileKind(it) !== "pdf");
  const pdfs = items.filter((it) => fileKind(it) === "pdf");

  // Original indices for the lightbox (so it works with mixed arrays)
  const idxOf = (target) => items.indexOf(target);

  const [firstVisual, ...restVisual] = visual;
  const headerLabel = visual.length === 0 ? "Dokumente" : "Bilder & Dokumente";

  return (
    <div className="mt-12 mb-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-4">{headerLabel}</p>

      {firstVisual && (
        <div className="relative mb-4">
          <MediaThumb item={firstVisual} kind={fileKind(firstVisual)} onOpen={() => setOpenIdx(idxOf(firstVisual))} size="large" index={idxOf(firstVisual)} total={items.length} />
          {firstVisual.caption && fileKind(firstVisual) !== "image" && (
            <p className="mt-2 text-xs text-neutral-500 italic">{firstVisual.caption}</p>
          )}
        </div>
      )}

      {restVisual.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
          {restVisual.map((it) => (
            <MediaThumb key={`v-${idxOf(it)}`} item={it} kind={fileKind(it)} onOpen={() => setOpenIdx(idxOf(it))} size="small" index={idxOf(it)} total={items.length} />
          ))}
        </div>
      )}

      {pdfs.length > 0 && (
        <div className={`grid sm:grid-cols-2 gap-3 ${visual.length > 0 ? "mt-6 pt-6 border-t border-brand-line" : ""}`} data-testid={`${testid}-pdfs`}>
          {pdfs.map((it) => <PdfCard key={`p-${idxOf(it)}`} item={it} index={idxOf(it)} />)}
        </div>
      )}

      <Lightbox items={items} index={openIdx} onClose={() => setOpenIdx(null)} onChange={setOpenIdx} />
    </div>
  );
}

export function Lightbox({ items, index, onClose, onChange }) {
  const open = index !== null && index !== undefined && items && items[index];

  const prev = useCallback(() => {
    if (index === null) return;
    let i = index - 1;
    while (i >= 0 && fileKind(items[i]) === "pdf") i--;
    if (i >= 0) onChange(i);
  }, [index, items, onChange]);
  const next = useCallback(() => {
    if (index === null) return;
    let i = index + 1;
    while (i < items.length && fileKind(items[i]) === "pdf") i++;
    if (i < items.length) onChange(i);
  }, [index, items, onChange]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, prev, next, onClose]);

  if (!open) return null;
  const it = items[index];
  const kind = fileKind(it);
  const url = kind === "video-url" ? null : resolveMedia(it.url);
  const ext = kind === "video-url" ? detectExternalVideo(it.url) : null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="lightbox">
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="lightbox-close" aria-label="Schließen" className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
        <X className="h-5 w-5" />
      </button>
      {items.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" aria-label="Zurück" className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" aria-label="Weiter" className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        {kind === "video-url" && ext ? (
          <div className="w-[min(92vw,1100px)] aspect-video bg-black">
            <iframe
              src={ext.embed + (ext.provider === "youtube" ? "&autoplay=1" : "?autoplay=1")}
              title={it.caption || "Video"}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        ) : kind === "video" ? (
          <video src={url} controls autoPlay className="max-w-full max-h-[82vh] bg-black" />
        ) : (
          <img src={url} alt={it.caption || ""} className="max-w-full max-h-[82vh] object-contain" />
        )}
        {it.caption && <p className="mt-4 text-sm text-white/80 max-w-2xl text-center">{it.caption}</p>}
      </div>
    </div>
  );
}

export default MediaGallery;
